import { useCallback, useEffect, useMemo, useState } from "react";
import { EUserRole, Listing, WithID } from "../../types/db";
import { isListingPublishable, ListingFEService } from "../../services/listing";
import { auth } from "../../services/firebase";
import { useQueryClient } from "react-query";

const listingService = new ListingFEService();

/** Takes care of fetching a listing and keeping it updated */
export const useListingInfo = (listingId: string) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [listing, setListing] = useState<WithID<Listing> | null>(null);
    const client = useQueryClient();

    const invalidateQueries = useCallback(async () => {
        await client.invalidateQueries(["user", "listings", EUserRole.HOST]);
        await client.invalidateQueries(["user", "listings", EUserRole.COHOST]);
    }, [client]);

    useEffect(() => {
        if (!listingId) {
            setListing(null);
            setIsLoading(false);
            return;
        }

        //update the current listing with the listing info from BE
        setIsLoading(true);
        setListing(null);//reset data
        setError('');//reset error state

        return listingService.getDocumentRef(listingId)
            .onSnapshot(
                (snap) => {
                    if (!snap.exists) {
                        //got deleted
                        setError('Do not exist');
                        setListing(null);
                        setIsLoading(false)
                    } else {
                        const changedListing = snap.data() as Listing;
                        setListing(curListing => ({
                            ...curListing,
                            ...changedListing,
                            id: snap.id,
                        }))
                    }
                    setIsLoading(false);
                },
                (error) => {
                    setError(error.message)
                    setIsLoading(false);
                })
    }, [listingId]);

    const _create = useCallback(async (data: Pick<Listing, "host" | 'duplicate_from' | 'parent_id'>): Promise<string> => {
        if (!auth.currentUser?.uid || auth.currentUser?.uid !== data.host.id) {
            console.warn('cannot create listing if user not logged in or not the owner/host');
            return Promise.reject("un-authorized")
        }
        setIsLoading(true);
        const newListingId = await listingService.create(data);
        await invalidateQueries();
        setIsLoading(false);
        return newListingId;
    }, [invalidateQueries])

    const _update = useCallback(async (data: Partial<Listing>): Promise<void> => {
        if (!listingId) {
            return Promise.reject('No listing to update')
        }
        if (!auth.currentUser?.uid
            // || (!!data.host?.id && auth.currentUser.uid !== data.host.id)
            || (!!listing?.host.id && auth.currentUser.uid !== listing.host.id)) {
            console.warn('cannot update listing if user not logged in or not the owner/host');
            return Promise.reject("un-authorized")
        }
        setIsLoading(true);
        await listingService.update(listingId, data);
        await invalidateQueries();
        setIsLoading(false);
    }, [invalidateQueries, listing?.host.id, listingId])

    const _delete = useCallback(async (): Promise<void> => {
        if (!listingId) {
            return Promise.reject('No listing to delete')
        }
        if (!auth.currentUser?.uid
            || (!!listing?.host.id && auth.currentUser.uid !== listing.host.id)) {
            console.warn('cannot update listing if user not logged in or not the owner/host');
            return Promise.reject("un-authorized")
        }
        setIsLoading(true);
        await listingService.delete(listingId);
        await invalidateQueries();
        setIsLoading(false);
    }, [invalidateQueries, listing?.host.id, listingId])

    const isPublishable = useMemo(() => {
        return isListingPublishable(listing)
    }, [listing])

    const publishIt = useCallback(() => {
        if (!isPublishable) {
            return Promise.reject("not-publishable")
        }
        return _update({ status: "in-review" });
    }, [_update, isPublishable])

    return {
        isLoading,
        error,
        listing,
        createNew: _create,
        updateIt: _update,
        deleteIt: _delete,
        isPublishable,
        publishIt
    }
}